import React, { memo, useCallback, useMemo, CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TimeInput } from "uikit";

import useDatePickerLocale from "../../../../hooks/useDatePickerLocale";
import {
	maxDate,
	setCalendarDay,
	setTypedTime,
} from "../../../../utils/clampDate";
import {
	StyledRow,
	StyledColumn,
	StyledP,
	BaseElementIdEnum,
	RadioButton,
	Calendar,
	CalendarNamespace,
} from "../../../common";
import BaseInputModal from "../BaseInputModal";
import { useKeyBind } from "../../../../hooks";
import { useAppContext } from "../../../../context";

interface BaseStyle {
	cursor?: CSSProperties["cursor"];
	error?: boolean;
}

const BaseTime = styled(TimeInput)<BaseStyle>`
	${({ cursor }) => cursor && `cursor:${cursor};`}
	input {
		${({ cursor }) => cursor && `cursor:${cursor};`}
	}
`;

const TimeSelect: React.FC<TimeSelect.Props> = ({
	value,
	setValue,
	error,
	setError,
}) => {
	const { t } = useTranslation();
	const { dateFns } = useAppContext();
	const onNow = useCallback(() => {
		setValue({ time: dateFns.toZonedTime(new Date()), on: "now" });
	}, [dateFns, setValue]);

	const onLater = useCallback(() => {
		setValue((prev) => ({ ...prev, on: "later" }));
	}, [setValue]);

	const disabled = useMemo(() => value.on === "now", [value.on]);

	const onChangeDatePicker = useCallback(
		(date: CalendarNamespace.Value) => {
			if (!date) return;
			if (disabled) return;
			const newDate = setCalendarDay(
				value.time ? new Date(value.time) : new Date(),
				date,
			);
			newDate.setSeconds(0, 0);

			const time = maxDate(newDate, new Date());

			setValue((prev) => ({ ...prev, time }));
		},
		[disabled, value.time, setValue],
	);

	const onChangeTime = useCallback(
		(value: TimeInput.Value) => {
			if (disabled) return;
			if (setError) setError(false);

			setValue((prev) => {
				const time =
					value && prev.time
						? setTypedTime(new Date(prev.time), value)
						: value || new Date();
				time.setSeconds(0, 0);
				return { ...prev, time };
			});
		},
		[disabled, setError, setValue],
	);
	const time = useMemo(() => value.time, [value.time]);

	const nextElementById = useCallback(
		(elemId: string): HTMLElement | null => {
			const elem = document.getElementById(elemId);
			if (elem) elem.focus();

			return elem;
		},
		[],
	);

	useKeyBind(["Tab"], (event) => {
		const target = event.target as HTMLInputElement;

		if (target.id === BaseElementIdEnum.SAVE) {
			nextElementById(BaseElementIdEnum.TIME_RADIO_BTN_NOW);
			event.preventDefault();
			event.stopPropagation();
		}

		if (target.id === BaseElementIdEnum.TIME_RADIO_BTN_NOW) {
			nextElementById(BaseElementIdEnum.TIME_RADIO_BTN_LATER);
			event.preventDefault();
			event.stopPropagation();
		}

		if (
			target.id === BaseElementIdEnum.TIME_RADIO_BTN_LATER &&
			value.on === "now"
		) {
			nextElementById(BaseElementIdEnum.CLOSE);
			event.preventDefault();
			event.stopPropagation();
		}
	});

	return (
		<StyledColumn
			gap="10px"
			p="15px"
			bgC="#ffffff"
			h="calc(252px - 84px)"
			justify="space-around"
		>
			<StyledRow gap="2rem">
				<RadioButton
					id={BaseElementIdEnum.TIME_RADIO_BTN_NOW}
					value={value.on === "now"}
					title={[`orderPage.order_form.order_time.now`, "Not now"]}
					onChange={onNow}
				/>

				<RadioButton
					id={BaseElementIdEnum.TIME_RADIO_BTN_LATER}
					value={value.on === "later"}
					title={[
						`orderPage.order_form.order_time.later`,
						"Pre order",
					]}
					onChange={onLater}
				/>
			</StyledRow>

			<StyledColumn gap="1rem">
				<StyledRow alignItems="center" gap="5px 10px">
					<StyledP>
						{t([`orderPage.order_form.time`, "Time"])}:
					</StyledP>

					<BaseTime
						id={BaseElementIdEnum.TIME_SELECT}
						cursor={disabled ? "not-allowed" : "pointer"}
						disabled={disabled}
						autoFocus
						step={60}
						value={time}
						onChange={onChangeTime}
						error={error}
					/>
				</StyledRow>
				<Calendar
					disabled={disabled}
					locale={useDatePickerLocale()}
					minDate={new Date()}
					value={value.time}
					onChange={onChangeDatePicker}
					error={error}
					width="130px"
				/>
			</StyledColumn>
		</StyledColumn>
	);
};

declare namespace TimeSelect {
	type OrderTime = "now" | "later";

	interface ScheduledTime {
		on: OrderTime;
		time: Date;
	}

	interface Props extends BaseInputModal.InputModalContent<ScheduledTime> {
		error?: boolean;
		setError?: React.Dispatch<React.SetStateAction<boolean>>;
	}
}

export const TimeSelectMemo = memo(TimeSelect);
export default TimeSelect;
